'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback, useSyncExternalStore } from 'react';
import type { DirectionsResult } from './mapbox/directions';
import { useLocationWidgetStore } from './store';
type RouteOrigin = {
  lat: number;
  lng: number;
};
export type DrivingRouteEntry = DirectionsResult & {
  /** activityId the route was fetched for */
  activityId: number;
  /** The location the route starts from — a route is only ever valid for this origin. */
  from: RouteOrigin;
};
type StoreState = {
  /** Keyed by activity **and** origin, so a route can never be read for another location. */
  routes: Map<string, DrivingRouteEntry>;
};

/**
 * A route belongs to one activity *and* one starting point. Keying on both is what makes a
 * stale read structurally impossible: when the user moves from Antwerp to Liège the lookup
 * key changes with them, so the previous city's polyline is simply not there anymore —
 * whether the new fetch is still in flight, was aborted, or failed (e.g. no Mapbox token).
 * Coordinates are rounded to ~11m so float noise doesn't invalidate the same place.
 */
function originKey(from: RouteOrigin) {
  return `${from.lat.toFixed(4)},${from.lng.toFixed(4)}`;
}
function routeKey(activityId: number, from: RouteOrigin) {
  return `${activityId}@${originKey(from)}`;
}
let state: StoreState = {
  routes: new Map()
};
const listeners = new Set<() => void>();
function emit() {
  for (const fn of listeners) fn();
}

/** Exported for tests; components subscribe through `useDrivingRouteStore`. */
export function subscribeToDrivingRoutes(cb: () => void) {
  return subscribe(cb);
}
function subscribe(cb: () => void) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}
function getSnapshot() {
  return state;
}
function selectRoute(routes: StoreState['routes'], activityId: number, from: RouteOrigin | null | undefined) {
  if (!from) return null;
  return routes.get(routeKey(activityId, from)) ?? null;
}

/** Store a fetched route for the activity/origin pair it was fetched for. */
export function setDrivingRoute(entry: DrivingRouteEntry) {
  // Storing the same route again must not notify: subscribers re-derive from the store, so
  // an emit that carries no new information can feed straight back into whatever triggered
  // the fetch. Cheap insurance against a re-render loop.
  const current = state.routes.get(routeKey(entry.activityId, entry.from));
  if (current && current.distanceKm === entry.distanceKm) return;

  // The user is at one place at a time: drop routes fetched from a previous origin instead
  // of growing the map with entries no reader can ever look up again.
  const next = new Map([...state.routes].filter(([, route]) => originKey(route.from) === originKey(entry.from)));
  next.set(routeKey(entry.activityId, entry.from), entry);
  state = {
    routes: next
  };
  emit();
}

/**
 * Read the route for an activity as seen from `from`. Exported for tests; components read
 * through `useDrivingRouteStore`, which resolves the origin from the shared widget store.
 */
export function getDrivingRoute(activityId: number, from: RouteOrigin | null) {
  return selectRoute(state.routes, activityId, from);
}

/** Hook to read the driving route store from any component */
export function useDrivingRouteStore() {
  const $ = _c(5);
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const location = useLocationWidgetStore(_temp);
  let t0;
  if ($[0] !== location || $[1] !== snapshot.routes) {
    t0 = activityId => selectRoute(snapshot.routes, activityId, location);
    $[0] = location;
    $[1] = snapshot.routes;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const getRoute = t0;
  let t1;
  if ($[3] !== getRoute) {
    t1 = {
      getRoute
    };
    $[3] = getRoute;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
}
function _temp(store) {
  return store.location;
}